import { CategoryPackDeepDiveContainer } from './SegmentPackDeepDive.Styled';
import { Button, Grid, Typography } from '@mui/material';
import NestedTable from './NestedTable';
import { DeepDive, ToggleRedWhite } from '../../../../assets/images/UI-dashboard';
import { BrandDropdown } from './BrandDropdown';
import { useAppDispatch, useAppSelector } from '../../../../state/hooks';
import { RootState } from '../../../../state/store';
import {
  fetchDeepDiveDownloadData,
  fetchSegmentDeepDive,
  setDeepdiveToggle,
} from '../../../../features/outputSegmentDeepDive/outputSegmentDeepDiveSlice';
import { SCENARIO_OUTPUT_DATA } from '../../../../utils/dataConstants';
import { hideLoader, showLoader } from '../../../../features/ui/loaderSlice';
import { SelectMapperKeys, ViewByMapperKeysForDeepdive } from '../../../../types/common';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { InputTextNote } from '../../../../components/FormComponent/FormStyles.Styled';

interface ApiResponse {
  [key: string]: string[];
}

export const SegmentPackDeepDive = () => {
  const { segments, selectedDeepdiveToggle } = useAppSelector((state: RootState) => state.outputSegmentDeepDive);
  const dispatch = useAppDispatch();
  const { selectedToggle, selectedViewBy, enabledKeys } = useAppSelector((state: RootState) => state.outputFilters);
  const { selectedScenarios } = useAppSelector((state: RootState) => state.outputSegmentDeepDive);
  const { selectedBrands } = useAppSelector((state: RootState) => state.brand);

  const transformDataNew = (input: Record<string, any>): any[] => {
    const scenarios = Object.keys(input);
    const output = new Map();

    const mergeScenarioData = (base: any, scenarioKey: any, data: any) => {
      for (const key of Object.keys(data)) {
        if (key === 'pack') continue;
        base[`${scenarioKey}_${key}`] = data[key];
      }
    };

    const mergePackData = (packMap: any, scenarioKey: any, packs: any) => {
      for (const pack of packs) {
        const key = pack.pack_size;
        if (!packMap.has(key)) {
          packMap.set(key, { pack_size: key });
        }
        const existingPack = packMap.get(key);
        mergeScenarioData(existingPack, scenarioKey, pack);
      }
    };

    for (const scenarioKey of scenarios) {
      for (const segments of input[scenarioKey]) {
        const segmentKey = segments.segment;

        if (!output.has(segmentKey)) {
          output.set(segmentKey, {
            segment: segmentKey,
            pack: new Map(),
          });
        }
        const segmentEntry = output.get(segmentKey);

        mergeScenarioData(segmentEntry, scenarioKey, segments);

        mergePackData(segmentEntry.pack, scenarioKey, segments.pack);
      }
    }

    return Array.from(output.values()).map((segmentEntry) => ({
      ...segmentEntry,
      pack: Array.from(segmentEntry.pack.values()),
    }));
  };

  const handleToggleChange = async () => {
    await dispatch(showLoader());
    await dispatch(setDeepdiveToggle(selectedDeepdiveToggle === 'rgm' ? 'finance' : 'rgm'));
    const payload = {
      scenario_1: selectedScenarios.base,
      scenario_2: selectedScenarios.child,
      brand: selectedBrands,
      bottler: enabledKeys.bottler,
      state: enabledKeys.state,
      select: SCENARIO_OUTPUT_DATA.SELECT_MAPPER[selectedToggle as SelectMapperKeys],
      view_by: SCENARIO_OUTPUT_DATA.VIEW_BY_MAPPER_DEEPDIVE[selectedViewBy as ViewByMapperKeysForDeepdive],
    };
    await dispatch(fetchSegmentDeepDive(payload));
    await dispatch(hideLoader());
  };

  const handleDownload = async () => {
    const payload = {
      scenario_1: selectedScenarios.base,
      scenario_2: selectedScenarios.child,
      select: SCENARIO_OUTPUT_DATA.SELECT_MAPPER[selectedToggle as SelectMapperKeys],
      view_by: SCENARIO_OUTPUT_DATA.VIEW_BY_MAPPER_DEEPDIVE[selectedViewBy as ViewByMapperKeysForDeepdive],
    };
    const response = await dispatch(fetchDeepDiveDownloadData(payload));
    if (response.payload) {
      const val = response.payload;
      downloadExcel(val, 'Segment_Deepdive_Data');
    }
  };

  const keyMapper: { [key: string]: string } = {
    // scenario_name: 'Scenario Name',
    // base_price: 'Base Price',
    // bottler: 'Bottler',
    // bottler_gp_base: 'Bottler GP Base',
    // brand: 'Brand',
    // category: 'Category',
    // scenario_type: "Scenario Type ('base')",
  };

  const noteMapper = {
    Absolute:
      'Compare two scenarios with their absolute and growth values vs base, aiding in evaluating business impact.',
    Incremental:
      'Compare two scenarios with their change and growth values vs base, aiding in evaluating volume shifts (cannibalization/sourcing)',
    Percent:
      'Compare two scenarios with their change and growth values vs base, aiding in evaluating volume shifts (cannibalization/sourcing)',
  };

  const downloadExcel = (data: ApiResponse, fileName: string) => {
    const transformedData = data.scenario_name.map((_, index) => {
      const row: { [key: string]: string } = {};
      Object.keys(data).forEach((key) => {
        const mappedKey = keyMapper[key] || key;
        row[mappedKey] = data[key][index];
      });
      return row;
    });

    const worksheet = XLSX.utils.json_to_sheet(transformedData);

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');

    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
    saveAs(blob, `${fileName}.xlsx`);
  };

  return (
    <Grid>
      <CategoryPackDeepDiveContainer>
        <Grid style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Grid
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignContent: 'center',
              alignItems: 'center',
            }}
          >
            <img src={DeepDive} alt="deepdive" />
            <Typography
              style={{
                fontWeight: 600,
                fontSize: '12.71px',
                lineHeight: '16.78px',
                marginLeft: '10px',
              }}
            >
              Segment and Pack Deepdive
            </Typography>
          </Grid>

          <Grid
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignContent: 'center',
              alignItems: 'center',
            }}
          >
            <Grid
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                whiteSpace: 'nowrap',
                marginLeft: '0%',
              }}
              item
            >
              <Grid>
                <BrandDropdown />
              </Grid>
              <Grid
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignContent: 'center',
                  alignItems: 'center',
                  marginLeft: '10px',
                }}
              >
                <Typography
                  style={{
                    fontWeight: selectedDeepdiveToggle === 'rgm' ? 600 : 500,
                    fontSize: 10,
                    lineHeight: '15px',
                    marginLeft: '5px',
                    opacity: selectedDeepdiveToggle === 'rgm' ? '100%' : '60%',
                  }}
                >
                  RGM
                </Typography>

                <img
                  style={{
                    cursor: 'pointer',
                    marginLeft: '5px',
                    transform: selectedDeepdiveToggle === 'rgm' ? 'rotate(0deg)' : 'rotate(180deg)',
                  }}
                  src={ToggleRedWhite}
                  alt="toggle"
                  onClick={handleToggleChange}
                />

                <Typography
                  style={{
                    fontWeight: selectedDeepdiveToggle !== 'rgm' ? 600 : 500,
                    fontSize: 10,
                    lineHeight: '15px',
                    marginLeft: '5px',
                    opacity: selectedDeepdiveToggle !== 'rgm' ? '100%' : '60%',
                  }}
                >
                  Finance
                </Typography>
              </Grid>
            </Grid>
            <Button
              style={{
                minWidth: '89.87px',
                border: '0.91px solid #E81C0E',
                color: '#E81C0E',
                background: 'white',
                borderRadius: '36.31px',
                marginLeft: '15px',

                fontSize: '10.89px',
                lineHeight: '13.28px',
                fontWeight: 600,
                height: '22.7px',
              }}
              onClick={handleDownload}
            >
              Download
            </Button>
          </Grid>
        </Grid>
        <Grid>
          <InputTextNote
            style={{
              marginTop: '5px',
              color: 'red',
              fontWeight: 700,
              fontSize: '9px',
            }}
          >
            Note- {noteMapper[selectedViewBy]}
          </InputTextNote>
        </Grid>
        <hr style={{ marginTop: '20px', marginBottom: '20px', opacity: '0.3' }} />
        <Grid style={{ marginTop: '10px' }}>
          <NestedTable data={transformDataNew(segments)} />
        </Grid>
      </CategoryPackDeepDiveContainer>
    </Grid>
  );
};
